// eRecords list pages for services, escalation policies, users, schedules and
// rotations. They replace upstream's lists at the same URLs and reuse
// upstream's create dialogs.
import React, { Suspense, useState } from 'react'
import { gql, useQuery } from 'urql'
import { AvatarGroup, Button, Chip, Tooltip, Typography } from '@mui/material'
import {
  Add,
  Check,
  Favorite,
  Group,
  Layers,
  PhoneInTalk,
  RotateRight,
  Today,
  VpnKey,
} from '@mui/icons-material'
import { useURLParam } from '../actions'
import { UserAvatar } from '../util/avatars'
import { useSessionInfo } from '../util/RequireConfig'
import UserCreateDialog from '../users/UserCreateDialog'
import RotationCreateDialog from '../rotations/RotationCreateDialog'
import { SeverityChip } from './AlertsPage'
import { PolicyDialog, ScheduleDialog, ServiceDialog } from './EditDialogs'
import {
  ChipTabs,
  Empty,
  IconTile,
  PagedList,
  Page,
  Panel,
  Row,
  SearchBox,
  Toolbar,
  useBrand,
} from './ui'

// GoAlert's urql client suspends by default; these pages render their own loading states.
const noSuspense = { suspense: false }

type Who = { id: string; name: string }

function People(props: {
  users: Who[]
  max?: number
}): React.JSX.Element | null {
  if (!props.users.length) return null
  return (
    <Tooltip title={props.users.map((u) => u.name).join(', ')}>
      <AvatarGroup
        max={props.max ?? 3}
        sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 } }}
      >
        {props.users.map((u) => (
          <UserAvatar key={u.id} userID={u.id} />
        ))}
      </AvatarGroup>
    </Tooltip>
  )
}

function Fav({ on }: { on: boolean }): React.JSX.Element | null {
  return on ? (
    <Favorite
      sx={{
        color: 'text.secondary',
        fontSize: 16,
        verticalAlign: 'text-bottom',
        ml: 0.5,
      }}
    />
  ) : null
}

function useListControls(): {
  search: string
  setSearch: (v: string) => void
  only: string
  setOnly: (v: string) => void
  input: Record<string, unknown>
} {
  const [search, setSearch] = useURLParam<string>('search', '')
  const [only, setOnly] = useURLParam<string>('show', 'all')
  return {
    search,
    setSearch,
    only,
    setOnly,
    input: { search, favoritesOnly: only === 'favorites' },
  }
}

function Controls(props: {
  c: ReturnType<typeof useListControls>
  placeholder: string
  favorites?: boolean
}): React.JSX.Element {
  return (
    <Toolbar>
      <SearchBox
        value={props.c.search}
        onChange={(v) => props.c.setSearch(v)}
        placeholder={props.placeholder}
      />
      {props.favorites !== false && (
        <ChipTabs
          value={props.c.only}
          onChange={(v) => props.c.setOnly(v)}
          options={[
            { value: 'all', label: 'All' },
            { value: 'favorites', label: 'Favorites' },
          ]}
        />
      )}
    </Toolbar>
  )
}

function CreateButton(props: {
  label: string
  onClick: () => void
}): React.JSX.Element {
  return (
    <Button variant='contained' startIcon={<Add />} onClick={props.onClick}>
      {props.label}
    </Button>
  )
}

// ---------------------------------------------------------------- services

const servicesQuery = gql`
  query ErecordsServices($input: ServiceSearchOptions) {
    services(input: $input) {
      nodes {
        id
        name
        description
        isFavorite
        escalationPolicy {
          id
          name
        }
        onCallUsers {
          userID
          userName
          stepNumber
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`
const openAlertsQuery = gql`
  query ErecordsOpenAlertsByService {
    alerts(
      input: {
        filterByStatus: [StatusUnacknowledged, StatusAcknowledged]
        first: 500
      }
    ) {
      nodes {
        id
        status
        serviceID
      }
    }
  }
`

type ServiceNode = {
  id: string
  name: string
  description: string
  isFavorite: boolean
  escalationPolicy: { id: string; name: string } | null
  onCallUsers: { userID: string; userName: string; stepNumber: number }[]
}

export function ServicesPage(): React.JSX.Element {
  const brand = useBrand()
  const c = useListControls()
  const [create, setCreate] = useState(false)
  const [open] = useQuery({
    context: noSuspense,
    query: openAlertsQuery,
    requestPolicy: 'cache-and-network',
  })
  const counts = new Map<string, { unack: number; ack: number }>()
  for (const a of open.data?.alerts?.nodes ?? []) {
    const n = counts.get(a.serviceID) ?? { unack: 0, ack: 0 }
    if (a.status === 'StatusUnacknowledged') n.unack++
    else n.ack++
    counts.set(a.serviceID, n)
  }

  return (
    <Page
      title='Services'
      subtitle='Each service receives alerts (from Grafana, BVT, …) and hands them to an escalation policy.'
      actions={
        <CreateButton label='New service' onClick={() => setCreate(true)} />
      }
    >
      <Suspense>
        {create && <ServiceDialog onClose={() => setCreate(false)} />}
      </Suspense>
      <Controls c={c} placeholder='Search services' />
      <Panel flush>
        <PagedList<ServiceNode>
          query={servicesQuery}
          field='services'
          input={{ ...c.input, favoritesFirst: true }}
          empty={
            <Empty
              title='No services'
              text='Create one to start receiving alerts.'
            />
          }
          render={(nodes) =>
            nodes.map((s) => {
              const n = counts.get(s.id)
              const first = s.onCallUsers.filter((u) => u.stepNumber === 0)
              return (
                <Row
                  key={s.id}
                  to={`/services/${s.id}`}
                  leading={
                    <IconTile tone={n?.unack ? 'bad' : 'brand'}>
                      <VpnKey />
                    </IconTile>
                  }
                  title={
                    <React.Fragment>
                      {s.name}
                      <Fav on={s.isFavorite} />
                    </React.Fragment>
                  }
                  subtitle={
                    s.escalationPolicy
                      ? `Escalates via ${s.escalationPolicy.name}`
                      : 'No escalation policy'
                  }
                  meta={
                    <React.Fragment>
                      <SeverityChip name={s.name} />
                      <People
                        users={first.map((u) => ({
                          id: u.userID,
                          name: u.userName,
                        }))}
                      />
                      {n?.unack ? (
                        <Chip
                          size='small'
                          color='error'
                          label={`${n.unack} unacknowledged`}
                        />
                      ) : n?.ack ? (
                        <Chip
                          size='small'
                          variant='outlined'
                          color='warning'
                          label={`${n.ack} acknowledged`}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: brand.muted,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '& svg': { fontSize: 15, color: brand.ok },
                          }}
                        >
                          <Check />
                          All clear
                        </Typography>
                      )}
                    </React.Fragment>
                  }
                />
              )
            })
          }
        />
      </Panel>
    </Page>
  )
}

// ---------------------------------------------------------- escalation policies

const policiesQuery = gql`
  query ErecordsPolicies($input: EscalationPolicySearchOptions) {
    escalationPolicies(input: $input) {
      nodes {
        id
        name
        description
        isFavorite
        repeat
        steps {
          id
          delayMinutes
          actions {
            type
            displayInfo {
              ... on DestinationDisplayInfo {
                text
              }
            }
          }
        }
        assignedTo {
          id
          name
          type
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type PolicyNode = {
  id: string
  name: string
  description: string
  isFavorite: boolean
  repeat: number
  steps: {
    id: string
    delayMinutes: number
    actions: { type: string; displayInfo: { text?: string } }[]
  }[]
  assignedTo: { id: string; name: string; type: string }[]
}

// actionLabel names a step target; Power Automate webhooks are Teams.
export function actionLabel(a: {
  type: string
  displayInfo: { text?: string }
}): string {
  const t = a.displayInfo.text || a.type
  if (a.type === 'builtin-webhook') {
    return /powerplatform|logic\.azure/.test(t) ? 'Teams (webhook)' : 'Webhook'
  }
  return t
}

// stepSummary renders "Dito → Teams after 15m" style text.
function stepSummary(p: PolicyNode): string {
  if (!p.steps.length) return 'No steps yet'
  let at = 0
  const parts = p.steps.map((st, i) => {
    const who = st.actions.map(actionLabel).join(' + ') || 'nobody'
    const s = i === 0 ? who : `${who} after ${at}m`
    at += st.delayMinutes
    return s
  })
  return parts.join(' → ') + (p.repeat ? ` · repeats ${p.repeat}×` : '')
}

export function PoliciesPage(): React.JSX.Element {
  const c = useListControls()
  const [create, setCreate] = useState(false)
  return (
    <Page
      title='Escalation policies'
      subtitle='Who gets notified, in which order, and how long to wait before the next step.'
      actions={
        <CreateButton label='New policy' onClick={() => setCreate(true)} />
      }
    >
      <Suspense>
        {create && <PolicyDialog onClose={() => setCreate(false)} />}
      </Suspense>
      <Controls c={c} placeholder='Search escalation policies' />
      <Panel flush>
        <PagedList<PolicyNode>
          query={policiesQuery}
          field='escalationPolicies'
          input={c.input}
          empty={<Empty title='No escalation policies' />}
          render={(nodes) =>
            nodes.map((p) => {
              const calls = p.steps.some((st) =>
                st.actions.some((a) =>
                  [
                    'builtin-user',
                    'builtin-schedule',
                    'builtin-rotation',
                  ].includes(a.type),
                ),
              )
              const services = p.assignedTo.filter((t) => t.type === 'service')
              return (
                <Row
                  key={p.id}
                  to={`/escalation-policies/${p.id}`}
                  leading={
                    <IconTile tone={calls ? 'brand' : 'muted'}>
                      <Layers />
                    </IconTile>
                  }
                  title={
                    <React.Fragment>
                      {p.name}
                      <Fav on={p.isFavorite} />
                    </React.Fragment>
                  }
                  subtitle={stepSummary(p)}
                  meta={
                    <React.Fragment>
                      <Chip
                        size='small'
                        variant='outlined'
                        label={`${services.length} service${services.length === 1 ? '' : 's'}`}
                      />
                      <Chip
                        size='small'
                        color={calls ? 'error' : 'default'}
                        variant={calls ? 'filled' : 'outlined'}
                        icon={calls ? <PhoneInTalk /> : undefined}
                        label={calls ? 'Notifies people' : 'Messages only'}
                      />
                    </React.Fragment>
                  }
                />
              )
            })
          }
        />
      </Panel>
    </Page>
  )
}

// ---------------------------------------------------------------- users

const usersQuery = gql`
  query ErecordsUsers($input: UserSearchOptions) {
    users(input: $input) {
      nodes {
        id
        name
        email
        role
        isFavorite
        contactMethods {
          id
          disabled
          dest {
            type
            args
          }
        }
        onCallOverview {
          serviceCount
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type UserNode = {
  id: string
  name: string
  email: string
  role: string
  isFavorite: boolean
  contactMethods: {
    id: string
    disabled: boolean
    dest: { type: string; args: Record<string, string> }
  }[]
  onCallOverview: { serviceCount: number }
}

export function UsersPage(): React.JSX.Element {
  const brand = useBrand()
  const { isAdmin } = useSessionInfo()
  const c = useListControls()
  const [create, setCreate] = useState(false)
  return (
    <Page
      title='Users'
      subtitle='People come from Microsoft Entra; their phone numbers and photos sync automatically.'
      actions={
        isAdmin ? (
          <CreateButton label='Local user' onClick={() => setCreate(true)} />
        ) : undefined
      }
    >
      <Suspense>
        {create && <UserCreateDialog onClose={() => setCreate(false)} />}
      </Suspense>
      <Controls c={c} placeholder='Search people by name or email' />
      <Panel flush>
        <PagedList<UserNode>
          query={usersQuery}
          field='users'
          input={c.input}
          empty={<Empty title='No users found' />}
          render={(nodes) =>
            nodes.map((u) => {
              const voice = u.contactMethods.find(
                (cm) => cm.dest.type === 'builtin-twilio-voice',
              )
              const phone =
                voice?.dest.args.phone_number ||
                u.contactMethods.find(
                  (cm) => cm.dest.type === 'builtin-twilio-sms',
                )?.dest.args.phone_number
              return (
                <Row
                  key={u.id}
                  to={`/users/${u.id}`}
                  leading={
                    <UserAvatar userID={u.id} sx={{ width: 40, height: 40 }} />
                  }
                  title={
                    <React.Fragment>
                      {u.name}
                      <Fav on={u.isFavorite} />
                    </React.Fragment>
                  }
                  subtitle={[u.email, phone].filter(Boolean).join(' · ')}
                  meta={
                    <React.Fragment>
                      {voice?.disabled && (
                        <Chip
                          size='small'
                          color='warning'
                          variant='outlined'
                          label='Calls paused'
                        />
                      )}
                      {u.onCallOverview.serviceCount > 0 ? (
                        <Chip
                          size='small'
                          color='primary'
                          variant='outlined'
                          label={`On call · ${u.onCallOverview.serviceCount} service${u.onCallOverview.serviceCount === 1 ? '' : 's'}`}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 12, color: brand.muted }}>
                          Not on call
                        </Typography>
                      )}
                      {u.role === 'admin' && (
                        <Chip size='small' variant='outlined' label='Admin' />
                      )}
                    </React.Fragment>
                  }
                />
              )
            })
          }
        />
      </Panel>
    </Page>
  )
}

// ---------------------------------------------------------------- schedules

const schedulesQuery = gql`
  query ErecordsSchedules(
    $input: ScheduleSearchOptions
    $start: ISOTimestamp!
    $end: ISOTimestamp!
  ) {
    schedules(input: $input) {
      nodes {
        id
        name
        description
        isFavorite
        timeZone
        shifts(start: $start, end: $end) {
          userID
          user {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type ScheduleNode = {
  id: string
  name: string
  description: string
  isFavorite: boolean
  timeZone: string
  shifts: { userID: string; user: Who | null }[]
}

export function SchedulesPage(): React.JSX.Element {
  const brand = useBrand()
  const c = useListControls()
  const [create, setCreate] = useState(false)
  const [window] = useState(() => {
    const now = new Date()
    return {
      start: now.toISOString(),
      end: new Date(now.getTime() + 60_000).toISOString(),
    }
  })
  return (
    <Page
      title='Schedules'
      subtitle='Calendars of who is on call when. Escalation policies can page whoever a schedule says is on call.'
      actions={
        <CreateButton label='New schedule' onClick={() => setCreate(true)} />
      }
    >
      <Suspense>
        {create && <ScheduleDialog onClose={() => setCreate(false)} />}
      </Suspense>
      <Controls c={c} placeholder='Search schedules' />
      <Panel flush>
        <PagedList<ScheduleNode>
          query={schedulesQuery}
          field='schedules'
          input={c.input}
          variables={window}
          empty={
            <Empty
              title='No schedules yet'
              text='Without schedules, escalation policies page people directly.'
            />
          }
          render={(nodes) =>
            nodes.map((s) => {
              const onCall = s.shifts.map(
                (sh) => sh.user ?? { id: sh.userID, name: 'Unknown' },
              )
              return (
                <Row
                  key={s.id}
                  to={`/schedules/${s.id}`}
                  leading={
                    <IconTile>
                      <Today />
                    </IconTile>
                  }
                  title={
                    <React.Fragment>
                      {s.name}
                      <Fav on={s.isFavorite} />
                    </React.Fragment>
                  }
                  subtitle={s.description || s.timeZone}
                  meta={
                    onCall.length ? (
                      <React.Fragment>
                        <People users={onCall} />
                        <Typography sx={{ fontSize: 13 }}>
                          {onCall.map((u) => u.name).join(', ')} on call
                        </Typography>
                      </React.Fragment>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: brand.muted }}>
                        Nobody on call now
                      </Typography>
                    )
                  }
                />
              )
            })
          }
        />
      </Panel>
    </Page>
  )
}

// ---------------------------------------------------------------- rotations

const rotationsQuery = gql`
  query ErecordsRotations($input: RotationSearchOptions) {
    rotations(input: $input) {
      nodes {
        id
        name
        description
        isFavorite
        type
        shiftLength
        activeUserIndex
        users {
          id
          name
        }
        nextHandoffTimes(num: 1)
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type RotationNode = {
  id: string
  name: string
  description: string
  isFavorite: boolean
  type: 'monthly' | 'weekly' | 'daily' | 'hourly'
  shiftLength: number
  activeUserIndex: number
  users: Who[]
  nextHandoffTimes: string[]
}

const unitOf: Record<RotationNode['type'], string> = {
  monthly: 'month',
  weekly: 'week',
  daily: 'day',
  hourly: 'hour',
}

export function RotationsPage(): React.JSX.Element {
  const brand = useBrand()
  const c = useListControls()
  const [create, setCreate] = useState(false)
  return (
    <Page
      title='Rotations'
      subtitle='People taking turns: each person is on call for one shift, then it hands off to the next.'
      actions={
        <CreateButton label='New rotation' onClick={() => setCreate(true)} />
      }
    >
      <Suspense>
        {create && <RotationCreateDialog onClose={() => setCreate(false)} />}
      </Suspense>
      <Controls c={c} placeholder='Search rotations' />
      <Panel flush>
        <PagedList<RotationNode>
          query={rotationsQuery}
          field='rotations'
          input={c.input}
          empty={<Empty title='No rotations yet' />}
          render={(nodes) =>
            nodes.map((r) => {
              const active = r.users[r.activeUserIndex]
              const next = r.nextHandoffTimes[0]
              const unit = unitOf[r.type] ?? r.type
              return (
                <Row
                  key={r.id}
                  to={`/rotations/${r.id}`}
                  leading={
                    <IconTile>
                      <RotateRight />
                    </IconTile>
                  }
                  title={
                    <React.Fragment>
                      {r.name}
                      <Fav on={r.isFavorite} />
                    </React.Fragment>
                  }
                  subtitle={`${r.shiftLength} ${unit}${r.shiftLength === 1 ? '' : 's'} per shift · ${r.users.length} ${r.users.length === 1 ? 'person' : 'people'}`}
                  meta={
                    <React.Fragment>
                      <People users={r.users} max={4} />
                      {active ? (
                        <Typography sx={{ fontSize: 13 }}>
                          <b>{active.name}</b> now
                          {next && (
                            <span style={{ color: brand.muted }}>
                              {' '}
                              · hands off{' '}
                              {new Date(next).toLocaleString([], {
                                weekday: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          )}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: brand.muted }}>
                          <Group
                            sx={{ fontSize: 14, verticalAlign: 'text-bottom' }}
                          />{' '}
                          No one in it yet
                        </Typography>
                      )}
                    </React.Fragment>
                  }
                />
              )
            })
          }
        />
      </Panel>
    </Page>
  )
}
